import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Customer } from '@/types';
import { Search, UserPlus, AlertCircle, User, Phone, FileText } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { fetchCustomersFromApi } from '@/services/customerApiService';
import { createCustomerOnErp } from '@/services/customerCreateService';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useApp } from '@/contexts/AppContext';
import { useToast } from '@/hooks/use-toast';

interface CustomerSelectorProps {
  onSelectCustomer: (customer: Customer) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CustomerSelector: React.FC<CustomerSelectorProps> = ({ 
  onSelectCustomer,
  open,
  onOpenChange
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { currentUser } = useApp();
  const { toast } = useToast();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerDocument, setNewCustomerDocument] = useState('');
  const [newCustomerPhone, setNewCustomerPhone] = useState('');

  useEffect(() => {
    if (open) {
      loadCustomers();
    }
  }, [open]);
  
  const loadCustomers = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Primeiro verificamos se há clientes no localStorage
      const storedCustomers = localStorage.getItem('restaurant_customers');
      if (storedCustomers) {
        const parsed = JSON.parse(storedCustomers);
        setCustomers(parsed);
        setFilteredCustomers(parsed);
        setIsLoading(false);
        return;
      }
      
      // Caso contrário, tentamos buscar da API
      const apiCustomers = await fetchCustomersFromApi();
      if (apiCustomers && apiCustomers.length > 0) {
        setCustomers(apiCustomers);
        setFilteredCustomers(apiCustomers);
        localStorage.setItem('restaurant_customers', JSON.stringify(apiCustomers));
      } else {
        // Se não houver clientes da API, usamos alguns dados de exemplo
        const mockCustomers: Customer[] = [
          { 
            id: '1', 
            name: 'Cliente Padrão', 
            document: '000.000.000-00',
            phone: '(00) 00000-0000' 
          },
          { 
            id: '2', 
            name: 'Maria Silva', 
            document: '123.456.789-00',
            phone: '(11) 98765-4321' 
          },
          { 
            id: '3', 
            name: 'João Oliveira', 
            document: '987.654.321-00',
            phone: '(11) 91234-5678' 
          }
        ];
        setCustomers(mockCustomers);
        setFilteredCustomers(mockCustomers);
      }
    } catch (err) {
      setError("Erro ao carregar clientes.");
      console.error("Erro ao carregar clientes:", err);
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    // Filtrar clientes quando o termo de busca mudar
    if (!searchTerm) {
      setFilteredCustomers(customers);
      return;
    }
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    const filtered = customers.filter(customer => 
      customer.name?.toLowerCase().includes(lowerSearchTerm) ||
      customer.document?.toLowerCase().includes(lowerSearchTerm) ||
      customer.phone?.toLowerCase().includes(lowerSearchTerm)
    );
    
    setFilteredCustomers(filtered);
  }, [searchTerm, customers]);
  
  const handleSelectCustomer = (customer: Customer) => {
    onSelectCustomer(customer);
    onOpenChange(false);
  };

  const handleCreateCustomer = async () => {
    const name = newCustomerName.trim();
    if (!name) {
      toast({
        title: 'Nome obrigatório',
        description: 'Informe o nome do cliente.',
        variant: 'destructive',
      });
      return;
    }

    setIsCreating(true);
    try {
      const created = await createCustomerOnErp({
        name,
        document: newCustomerDocument.trim() || undefined,
        phone: newCustomerPhone.trim() || undefined,
        vendedorId: currentUser?.vendedor_id ? Number(currentUser.vendedor_id) : 0,
      });

      // Atualiza lista local e cache
      const nextCustomers = [created, ...customers];
      setCustomers(nextCustomers);
      setFilteredCustomers(nextCustomers);
      localStorage.setItem('restaurant_customers', JSON.stringify(nextCustomers));

      toast({
        title: 'Cliente criado',
        description: `${created.name} foi cadastrado com sucesso.`,
      });

      // Seleciona automaticamente
      onSelectCustomer(created);
      setCreateDialogOpen(false);
      onOpenChange(false);

      // Reset
      setNewCustomerName('');
      setNewCustomerDocument('');
      setNewCustomerPhone('');
    } catch (err) {
      toast({
        title: 'Erro ao criar cliente',
        description: err instanceof Error ? err.message : 'Não foi possível cadastrar o cliente.',
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Selecionar Cliente</DialogTitle>
          </DialogHeader>
          <div className="flex items-center space-x-2 mb-2">
            <Search className="h-4 w-4 text-gray-500 flex-shrink-0" />
            <Input
              type="search"
              placeholder="Buscar cliente por nome, CPF ou telefone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-8"
              autoFocus
            />
          </div>
          
          {isLoading ? (
            <div className="text-center py-4">
              <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2"></div>
              <p className="text-sm">Carregando clientes...</p>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center p-4 text-amber-600 bg-amber-50 rounded-md">
              <AlertCircle className="h-4 w-4 mr-2" />
              <span className="text-xs">{error}</span>
            </div>
          ) : (
            <ScrollArea className="h-[300px] pr-2">
              {filteredCustomers.length > 0 ? (
                <div className="space-y-2">
                  {filteredCustomers.map(customer => (
                    <div
                      key={customer.id}
                      className="p-3 border rounded-lg cursor-pointer transition-all hover:bg-gray-50"
                      onClick={() => handleSelectCustomer(customer)}
                    >
                      <div className="flex items-center">
                        <div className="bg-gray-100 p-2 rounded-full">
                          <User className="h-4 w-4 text-gray-500" />
                        </div>
                        <div className="ml-3 flex-1">
                          <p className="font-medium text-sm">{customer.name}</p>
                          <div className="flex items-center space-x-3 text-xs text-gray-500 mt-1">
                            {customer.document && (
                              <div className="flex items-center">
                                <FileText className="h-3 w-3 mr-1" />
                                <span>{customer.document}</span>
                              </div>
                            )}
                            {customer.phone && (
                              <div className="flex items-center">
                                <Phone className="h-3 w-3 mr-1" />
                                <span>{customer.phone}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-500 mb-2">Nenhum cliente encontrado</p>
                  <Button variant="outline" size="sm" className="mx-auto" onClick={() => setCreateDialogOpen(true)}>
                    <UserPlus className="h-4 w-4 mr-1" />
                    Criar Novo Cliente
                  </Button>
                </div>
              )}
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Novo Cliente</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Nome</Label>
              <Input value={newCustomerName} onChange={(e) => setNewCustomerName(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Documento (CPF/CNPJ)</Label>
              <Input value={newCustomerDocument} onChange={(e) => setNewCustomerDocument(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Telefone</Label>
              <Input value={newCustomerPhone} onChange={(e) => setNewCustomerPhone(e.target.value)} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} disabled={isCreating}>
              Cancelar
            </Button>
            <Button onClick={handleCreateCustomer} disabled={isCreating}>
              {isCreating ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CustomerSelector;
